import { Placeholder } from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import type { Editor, JSONContent } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import type { ComposerTokenAttrs } from "./chat-composer-token";
import { ComposerTokenNode } from "./chat-composer-token";
import {
  CLOSED_STATE,
  computeSlashStateFromText,
} from "./chat-composer-slash-state";
import type {
  SlashAgentOption,
  SlashCommandOption,
  SlashSkillOption,
} from "./slash-command-data";
import "./chat-composer-editor.css";

/**
 * Slash menu state emitted by the Tiptap composer.
 * The parent uses this to position and filter the floating slash menu.
 *
 * Despite the name (kept for backwards compatibility), this state covers
 * BOTH triggers — `/` for the slash menu and `@` for the file-mention
 * menu. Consumers branch on `trigger` to render the right dropdown.
 */
export interface ChatComposerSlashState {
  /**
   * Whether a trigger query is currently active at the caret.
   * False means whatever menu is currently open should close.
   */
  open: boolean;

  /**
   * Which trigger character is active at the caret.
   * - "slash": `/`-prefixed patterns, agents, or comments menu.
   * - "skill": `$`-prefixed skill picker.
   * - "mention": `@`-prefixed file-mention picker (browser-workspace).
   */
  trigger: "slash" | "skill" | "mention";

  /**
   * Menu mode inferred from the typed trigger query.
   * Root lists slash patterns while scoped modes list agents, comments, or skills.
   */
  mode: "root" | "agents" | "comments" | "skills";

  /**
   * Query text typed after the trigger character.
   * Passed to the active dropdown for filtering.
   */
  query: string;

  /**
   * Document range containing the trigger text (e.g. `/foo` or `@bar`).
   * The selected menu item replaces this range with a token node.
   */
  range?: {
    from: number;
    to: number;
  };
}

/**
 * Structured submission payload extracted from the composer document.
 * The parent combines this with attachments and selected agents on submit.
 */
export interface ChatComposerSubmission {
  /**
   * Plain text content typed by the user.
   * Token nodes are excluded from this text field.
   */
  text: string;

  /**
   * Token nodes currently present in the document.
   * Used for agent routing and comment serialization.
   */
  tokens: ComposerTokenAttrs[];
}

/**
 * Imperative API exposed to the parent input component.
 * Needed so the floating slash menu can insert tokens at the current caret.
 */
export interface ChatComposerEditorRef {
  /**
   * Focuses the editor at the current selection.
   * Used after slash menu interactions and message submission.
   */
  focus: () => void;

  /**
   * Blurs the editor, dropping DOM focus from the contenteditable.
   * Used on mobile after submit so the soft keyboard dismisses and the
   * sent message + response become visible.
   */
  blur: () => void;

  /**
   * Clears all editor content and resets the document to an empty paragraph.
   * Called after successful message submission.
   */
  clear: () => void;

  /**
   * Removes the currently typed slash trigger text without touching tokens.
   * Used when the floating menu is dismissed with escape or outside click.
   */
  clearSlashQuery: () => void;

  /**
   * Returns the editor DOM element used as the menu anchor.
   * The parent uses this to position the floating slash menu.
   */
  getAnchorElement: () => HTMLElement | null;

  /**
   * Inserts a real agent token at the active slash range.
   * Also removes the typed slash trigger text from the document.
   */
  insertAgentToken: (agent: SlashAgentOption) => void;

  /**
   * Inserts a comment token at the active slash range.
   * Used for explicit `/comment` note insertion.
   */
  insertCommentToken: (comment: string) => void;

  /**
   * Inserts a command token at the active slash range.
   * Preserves lightweight command metadata inside the editor.
   */
  insertCommandToken: (command: SlashCommandOption) => void;

  /**
   * Inserts a file mention token at the active `@` range.
   * `path` is stored in the token's `value` so the parent can serialize
   * the full path on submit; `name` is what the user sees in the chip.
   */
  insertFileToken: (file: { name: string; path: string }) => void;

  /**
   * Inserts a skill token at the active slash range. The skill's slug is
   * stored in `value` so the runtime can resolve the skill independently
   * of its display name.
   */
  insertSkillToken: (skill: SlashSkillOption) => void;

  /**
   * Extracts the current submission payload from the editor document.
   * The parent calls this during send.
   */
  getSubmission: () => ChatComposerSubmission;
}

/**
 * Props for the Tiptap chat composer.
 * Keeps the editor reusable while exposing only the state the parent needs.
 */
interface ChatComposerEditorProps {
  /**
   * Plain text value coming from parent state.
   * External updates replace the editable text while preserving inline tokens.
   */
  value: string;

  /**
   * Inline tokens controlled by the parent component.
   * This keeps submission state and external agent routing synchronized.
   */
  tokens?: ComposerTokenAttrs[];

  /**
   * Placeholder shown when the editor is empty.
   * Mirrors the prior textarea placeholder.
   */
  placeholder: string;

  /**
   * Whether the editor should use compact spacing.
   * The widget and mobile flows use a smaller layout.
   */
  isCompact?: boolean;

  /**
   * Called whenever the plain text content changes.
   * Keeps upstream chat state in sync with what the user typed.
   */
  onTextChange: (text: string) => void;

  /**
   * Called whenever token nodes change inside the document.
   * Used to keep selected agents aligned with inline agent tags.
   */
  onTokensChange: (tokens: ComposerTokenAttrs[]) => void;

  /**
   * Emits slash-trigger state derived from the current caret context.
   * The parent uses this to show and drive the floating menu.
   */
  onSlashStateChange: (state: ChatComposerSlashState) => void;

  /**
   * Callback used for Enter-to-send behavior.
   * Shift+Enter remains available for line breaks.
   */
  onSubmitShortcut: () => void;

  /**
   * Whether a bare Enter submits the message. Desktop passes `true`
   * (Enter sends, Shift+Enter newlines). Mobile passes `false` so the
   * on-screen Return key inserts a newline — sending is done via the Send
   * button — matching native messaging-app behavior on phones.
   * Defaults to `true`.
   */
  enterSubmits?: boolean;

  /**
   * Notifies the parent when the editor receives focus.
   * Lets the outer composer keep its existing focus ring behavior.
   */
  onFocus?: () => void;

  /**
   * Disables editor input while the model is streaming.
   * Prevents accidental edits mid-request.
   */
  disabled?: boolean;
}

function buildDocWithTokens(tokens: ComposerTokenAttrs[], text: string): JSONContent {
  const content: JSONContent[] = [
    ...tokens.map((token) => ({
      type: "composerToken",
      attrs: token,
    })),
  ];

  if (text) {
    content.push({
      type: "text",
      text,
    });
  }

  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content,
      },
    ],
  };
}

function extractSubmission(editor: Editor): ChatComposerSubmission {
  const tokens: ComposerTokenAttrs[] = [];
  let text = "";
  let sawBlock = false;

  editor.state.doc.descendants((node) => {
    if (node.type.name === "composerToken") {
      tokens.push(node.attrs as ComposerTokenAttrs);
      return false;
    }

    // Serialize line breaks so multi-line composition survives submit.
    // Without this, paragraph splits (Enter on mobile) and hard breaks
    // (Shift+Enter, or pasted multi-line text) collapse into a single line.
    if (node.type.name === "hardBreak") {
      text += "\n";
      return false;
    }

    if (node.isBlock) {
      if (sawBlock) {
        text += "\n";
      }
      sawBlock = true;
      return true;
    }

    if (node.isText) {
      text += node.text || "";
    }

    return true;
  });

  return {
    text: text.trim(),
    tokens,
  };
}

function resolveSlashState(editor: Editor): ChatComposerSlashState {
  const { from, empty } = editor.state.selection;
  if (!empty) {
    return CLOSED_STATE;
  }

  const textBefore = editor.state.doc.textBetween(0, from, "\n", " ");
  return computeSlashStateFromText(textBefore, from);
}

/**
 * Tiptap-based chat composer with inline token support.
 * Replaces the textarea so tags behave like real editor content.
 */
export const ChatComposerEditor = forwardRef<ChatComposerEditorRef, ChatComposerEditorProps>(
  function ChatComposerEditor({
    value,
    tokens = [],
    placeholder,
    isCompact = false,
    onTextChange,
    onTokensChange,
    onSlashStateChange,
    onSubmitShortcut,
    onFocus,
    disabled = false,
    enterSubmits = true,
  }, ref) {
    // Read the latest value inside ProseMirror's keydown handler, which is
    // created once by useEditor and would otherwise close over a stale prop.
    const enterSubmitsRef = useRef(enterSubmits);
    enterSubmitsRef.current = enterSubmits;
    const lastPlainTextRef = useRef(value);
    const lastSerializedTokensRef = useRef(JSON.stringify(tokens));
    const lastSlashStateRef = useRef<string>("");
    const serializedTokens = useMemo(() => JSON.stringify(tokens), [tokens]);

    const extensions = useMemo(
      () => [
        StarterKit.configure({
          blockquote: false,
          bulletList: false,
          codeBlock: false,
          code: false,
          heading: false,
          horizontalRule: false,
          listItem: false,
          orderedList: false,
        }),
        Placeholder.configure({
          placeholder,
        }),
        ComposerTokenNode,
      ],
      [placeholder]
    );

    const editor = useEditor({
      immediatelyRender: false,
      extensions,
      content: buildDocWithTokens(tokens, value),
      editable: !disabled,
      editorProps: {
        attributes: {
          class: `tiptap ${isCompact ? "min-h-[32px] text-sm leading-5" : "min-h-[52px] text-base leading-6"}`,
        },
        handleKeyDown: (_, event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            // Mobile: let ProseMirror handle Enter as a line break instead
            // of sending. Sending happens via the Send button there.
            if (!enterSubmitsRef.current) {
              return false;
            }
            event.preventDefault();
            onSubmitShortcut();
            return true;
          }

          return false;
        },
        handleDOMEvents: {
          focus: () => {
            onFocus?.();
            return false;
          },
        },
      },
      onUpdate: ({ editor: currentEditor }) => {
        const submission = extractSubmission(currentEditor);
        lastPlainTextRef.current = submission.text;
        lastSerializedTokensRef.current = JSON.stringify(submission.tokens);
        onTextChange(submission.text);
        onTokensChange(submission.tokens);

        const slashState = resolveSlashState(currentEditor);
        const serializedSlashState = JSON.stringify(slashState);
        if (serializedSlashState !== lastSlashStateRef.current) {
          lastSlashStateRef.current = serializedSlashState;
          onSlashStateChange(slashState);
        }
      },
      onSelectionUpdate: ({ editor: currentEditor }) => {
        const slashState = resolveSlashState(currentEditor);
        const serializedSlashState = JSON.stringify(slashState);
        if (serializedSlashState !== lastSlashStateRef.current) {
          lastSlashStateRef.current = serializedSlashState;
          onSlashStateChange(slashState);
        }
      },
    });

    useEffect(() => {
      if (!editor) {
        return;
      }

      editor.setEditable(!disabled);
    }, [disabled, editor]);

    useEffect(() => {
      if (!editor) {
        return;
      }

      if (
        value === lastPlainTextRef.current
        && serializedTokens === lastSerializedTokensRef.current
      ) {
        return;
      }

      const nextDoc = buildDocWithTokens(tokens, value);
      // `{ emitUpdate: false }`, not a bare `false`. Tiptap v2 took the flag
      // positionally; v3 takes an options object, so `false` destructures to
      // an empty object and `emitUpdate` falls back to its `true` default.
      // That turned this sync into a cycle: setContent -> onUpdate ->
      // onTextChange -> parent state -> new `value` -> setContent, which React
      // killed with "Maximum update depth exceeded" and took the whole
      // composer down with it.
      editor.commands.setContent(nextDoc, { emitUpdate: false });
      lastPlainTextRef.current = value;
      lastSerializedTokensRef.current = serializedTokens;

      const slashState = resolveSlashState(editor);
      const serializedSlashState = JSON.stringify(slashState);
      lastSlashStateRef.current = serializedSlashState;
      onSlashStateChange(slashState);
    }, [editor, onSlashStateChange, serializedTokens, tokens, value]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        editor?.commands.focus();
      },
      blur: () => {
        // Tiptap's blur command calls `view.dom.blur()` under the hood,
        // which releases focus from the contenteditable and lets the
        // mobile soft keyboard close.
        editor?.commands.blur();
      },
      clear: () => {
        if (!editor) {
          return;
        }

        editor.commands.setContent(buildDocWithTokens([], ""), { emitUpdate: false });
        lastPlainTextRef.current = "";
        lastSerializedTokensRef.current = JSON.stringify([]);
        onTextChange("");
        onTokensChange([]);
        onSlashStateChange(CLOSED_STATE);
      },
      clearSlashQuery: () => {
        if (!editor) {
          return;
        }

        const slashState = resolveSlashState(editor);
        if (slashState.range) {
          editor.commands.deleteRange(slashState.range);
        }
      },
      getAnchorElement: () => editor?.view.dom as HTMLElement | null,
      insertAgentToken: (agent) => {
        if (!editor) {
          return;
        }

        const slashState = resolveSlashState(editor);
        if (slashState.range) {
          editor.commands.deleteRange(slashState.range);
        }

        // Only one specialist may be active at a time — picking a new one
        // wipes any existing agent token from the composer (no stacked
        // "@Aria @Bob …" routing, which would confuse the user about which
        // agent is actually answering). See issue #981/#983.
        editor.commands.command(({ tr, state }) => {
          const toDelete: Array<{ from: number; to: number }> = [];
          state.doc.descendants((node, pos) => {
            if (
              node.type.name === "composerToken" &&
              node.attrs?.kind === "agent"
            ) {
              toDelete.push({ from: pos, to: pos + node.nodeSize });
            }
          });
          // Iterate in reverse so each delete doesn't shift later positions.
          for (let i = toDelete.length - 1; i >= 0; i -= 1) {
            tr.delete(toDelete[i].from, toDelete[i].to);
          }
          return true;
        });

        editor.commands.insertComposerToken({
          kind: "agent",
          id: agent.agent?.id || agent.id,
          label: agent.name,
          value: agent.name,
          icon: "bot",
        });
        editor.commands.focus();
      },
      insertCommentToken: (comment) => {
        if (!editor) {
          return;
        }

        const slashState = resolveSlashState(editor);
        if (slashState.range) {
          editor.commands.deleteRange(slashState.range);
        }

        editor.commands.insertComposerToken({
          kind: "comment",
          label: comment,
          value: comment,
          icon: "message-square-quote",
        });
        editor.commands.focus();
      },
      insertCommandToken: (command) => {
        if (!editor) {
          return;
        }

        const slashState = resolveSlashState(editor);
        if (slashState.range) {
          editor.commands.deleteRange(slashState.range);
        }

        editor.commands.insertComposerToken({
          kind: "command",
          id: command.id,
          label: command.title,
          value: command.title,
          icon: command.id === "summarize" ? "file-text" : "search-check",
        });
        editor.commands.focus();
      },
      insertFileToken: (file) => {
        if (!editor) {
          return;
        }

        const slashState = resolveSlashState(editor);
        if (slashState.range) {
          editor.commands.deleteRange(slashState.range);
        }

        editor.commands.insertComposerToken({
          kind: "file",
          // Store the path as id so de-duplication on submit is path-keyed.
          id: file.path,
          label: file.name,
          value: file.path,
          icon: "file-text",
        });
        editor.commands.focus();
      },
      insertSkillToken: (skill) => {
        if (!editor) {
          return;
        }

        const slashState = resolveSlashState(editor);
        if (slashState.range) {
          editor.commands.deleteRange(slashState.range);
        }

        editor.commands.insertComposerToken({
          kind: "skill",
          id: skill.id,
          label: skill.name,
          // Slug is the stable handle the backend can resolve to instructions.
          value: skill.slug,
          icon: "puzzle",
        });
        editor.commands.focus();
      },
      getSubmission: () => {
        if (!editor) {
          return { text: "", tokens: [] };
        }

        return extractSubmission(editor);
      },
    }), [editor, onSlashStateChange, onTextChange, onTokensChange]);

    if (!editor) {
      return null;
    }

    return (
      <div className="chat-composer-editor flex-1">
        <EditorContent editor={editor} />
      </div>
    );
  }
);
